import { useState, useEffect, useCallback } from 'react';
import { api, handleAuthFailure } from '../data/api';
import { TableWrap, TH, TD } from '../components/TableWrap';
import Btn from '../components/Btn';
import { useTheme } from '../context/ThemeContext';

const ACTION_STYLE = {
  created:        { color:"#059669", bg:"#D1FAE5", border:"#6EE7B7" },
  updated:        { color:"#2563EB", bg:"#DBEAFE", border:"#93C5FD" },
  deleted:        { color:"#DC2626", bg:"#FEF2F2", border:"#FCA5A5" },
  activated:      { color:"#059669", bg:"#D1FAE5", border:"#6EE7B7" },
  deactivated:    { color:"#D97706", bg:"#FEF3C7", border:"#FCD34D" },
  status_changed: { color:"#7C3AED", bg:"#EDE9FE", border:"#C4B5FD" },
  assigned:       { color:"#0891B2", bg:"#CFFAFE", border:"#67E8F9" },
  unassigned:     { color:"#D97706", bg:"#FEF3C7", border:"#FCD34D" },
  ota_pushed:     { color:"#0891B2", bg:"#CFFAFE", border:"#67E8F9" },
  bulk_upload:    { color:"#059669", bg:"#D1FAE5", border:"#6EE7B7" },
};

const ENTITY_ICON = { affiliate:"🤝", device:"🖥️", channel:"📡", ota:"📦" };

function ActionBadge({ action }) {
  const s = ACTION_STYLE[action] || { color:"#64748B", bg:"#F1F5F9", border:"#CBD5E1" };
  return (
    <span style={{
      display:"inline-block", fontSize:10, fontWeight:700,
      letterSpacing:"0.05em", textTransform:"uppercase",
      color:s.color, background:s.bg, border:`1px solid ${s.border}`,
      borderRadius:4, padding:"2px 7px", whiteSpace:"nowrap",
    }}>
      {action?.replace(/_/g,' ') || '—'}
    </span>
  );
}

function timeAgo(ts) {
  if (!ts) return '—';
  const m = Math.floor((Date.now() - new Date(ts)) / 60000);
  if (m < 1)  return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h/24)}d ago`;
}

function fmt(ts) {
  if (!ts) return '—';
  try {
    return new Date(ts).toLocaleString('en-GB', {
      day:'2-digit', month:'short', year:'numeric',
      hour:'2-digit', minute:'2-digit', second:'2-digit',
    });
  } catch { return ts; }
}

const inputStyle = {
  background:"var(--surface)", border:"1px solid var(--border)",
  borderRadius:6, padding:"7px 10px", fontSize:12,
  color:"var(--text)", fontFamily:"inherit", outline:"none",
};

export default function ActivityLog() {
  useTheme();
  const [logs, setLogs]           = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [selected, setSelected]   = useState(null);
  const [filterType, setFilterType]     = useState('');
  const [filterAction, setFilterAction] = useState('');
  const [searchInput, setSearchInput]   = useState('');
  const [search, setSearch]             = useState('');
  const [dateFrom, setDateFrom]         = useState('');
  const [dateTo, setDateTo]             = useState('');

  const [spinning, setSpinning] = useState(false);

  const load = useCallback(async () => {
    setSpinning(true); setLoading(true); setError(null);
    try {
      const params = {};
      if (filterType)   params.entity_type = filterType;
      if (filterAction) params.action      = filterAction;
      if (search)       params.search      = search;
      if (dateFrom)     params.date_from   = dateFrom;
      if (dateTo)       params.date_to     = dateTo;
      const rows = await api.activity.list(params);
      setLogs(rows);
      setSelected(prev => prev ? (rows.find(r => r.id === prev.id) ?? null) : null);
    } catch (e) {
      if (handleAuthFailure(e)) return;
      setError(e.message || 'Failed to load activity log');
    } finally {
      setLoading(false);
      setSpinning(false);
    }
  }, [filterType, filterAction, search, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const exportCSV = () => {
    if (!logs.length) return;
    const cols = ['created_at','actor','actor_role','entity_type','entity_id','action','detail'];
    const csv  = [cols.join(','), ...logs.map(l =>
      cols.map(c => `"${(l[c]??'').toString().replace(/"/g,'""')}"`).join(',')
    )].join('\n');
    const a = Object.assign(document.createElement('a'), {
      href: URL.createObjectURL(new Blob([csv], {type:'text/csv'})),
      download: `activity-${new Date().toISOString().slice(0,10)}.csv`,
    });
    a.click();
  };

  const clearFilters = () => { setFilterType(''); setFilterAction(''); setSearch(''); setSearchInput(''); setDateFrom(''); setDateTo(''); };

  return (
    <>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:18, paddingBottom:14, borderBottom:"1px solid var(--border)" }}>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <h1 style={{ fontSize:17, fontWeight:700, color:"var(--ink)" }}>Activity Log</h1>
          {loading && <span style={{ fontSize:11, color:"var(--text-dim)" }}>Loading…</span>}
          {!loading && !error && <span style={{ fontSize:11, color:"var(--text-dim)" }}>{logs.length} entries</span>}
        </div>
        <div style={{ display:"flex", gap:8 }}>
          <button onClick={load} style={{ background:"var(--surface)", border:"1px solid var(--border)", borderRadius:6, padding:"5px 12px", fontSize:11, color:"var(--text-sub)", cursor:"pointer", fontFamily:"inherit", display:"flex", alignItems:"center", gap:5 }}>
            <span style={{ display:"inline-block", animation: spinning ? "al-spin 0.7s linear infinite" : "none", lineHeight:1 }}>↻</span>
            Refresh
          </button>
          {/* <Btn onClick={() => { setLogs([]); setSelected(null); }}>Clear Entries</Btn> */}
          <Btn onClick={exportCSV}>Export CSV</Btn>
        </div>
        <style>{`@keyframes al-spin { to { transform: rotate(360deg); } }`}</style>
      </div>

      {error && (
        <div style={{ background:"var(--red-bg,#fef2f2)", border:"1px solid var(--red-border,#fca5a5)", borderRadius:7, padding:"10px 14px", fontSize:12, color:"var(--red,#dc2626)", marginBottom:14 }}>
          {error}
        </div>
      )}

      <div style={{ display:"flex", gap:8, marginBottom:14, flexWrap:"wrap" }}>
        <select value={filterType} onChange={e => setFilterType(e.target.value)} style={inputStyle}>
          <option value="">All Types</option>
          <option value="affiliate">Affiliates</option>
          <option value="device">Devices</option>
          <option value="channel">Channels</option>
          <option value="ota">OTA</option>
        </select>
        <select value={filterAction} onChange={e => setFilterAction(e.target.value)} style={inputStyle}>
          <option value="">All Actions</option>
          <option value="created">Created</option>
          <option value="updated">Updated</option>
          <option value="deleted">Deleted</option>
          <option value="activated">Activated</option>
          <option value="deactivated">Deactivated</option>
          <option value="status_changed">Status Changed</option>
          <option value="assigned">Assigned</option>
          <option value="unassigned">Unassigned</option>
          <option value="ota_pushed">OTA Pushed</option>
          <option value="bulk_upload">Bulk Upload</option>
        </select>
        <input
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
          onKeyDown={e => { if (e.key==='Enter') setSearch(searchInput); }}
          placeholder="Search ID, actor, detail…"
          style={{ ...inputStyle, flex:1, minWidth:180 }}
        />
        <Btn primary onClick={() => setSearch(searchInput)}>Apply</Btn>
        <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
          title="From date"
          style={{ ...inputStyle, width:130 }} />
        <span style={{ fontSize:11, color:"var(--text-dim)", alignSelf:"center" }}>→</span>
        <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
          title="To date"
          style={{ ...inputStyle, width:130 }} />
        {(filterType || filterAction || search || dateFrom || dateTo) && <Btn onClick={clearFilters}>Clear</Btn>}
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"1fr 280px", gap:10 }}>
        <TableWrap>
          <thead>
            <tr>
              <TH>When</TH>
              <TH>Actor</TH>
              <TH>Type</TH>
              <TH>Entity ID</TH>
              <TH>Action</TH>
              <TH>Detail</TH>
            </tr>
          </thead>
          <tbody>
            {logs.length ? logs.map(log => (
              <tr
                key={log.id}
                onClick={() => setSelected(log)}
                style={{ cursor:"pointer", background: selected?.id===log.id ? "var(--surface-alt)" : "transparent" }}
              >
                <TD mono sub style={{ fontSize:11, whiteSpace:"nowrap" }}>
                  <span title={fmt(log.created_at)}>{timeAgo(log.created_at)}</span>
                </TD>
                <TD style={{ fontSize:11 }}>
                  <div style={{ fontWeight:500, color:"var(--ink)" }}>{log.actor}</div>
                  <div style={{ fontSize:10, color:"var(--text-dim)", textTransform:"uppercase" }}>{log.actor_role}</div>
                </TD>
                <TD style={{ fontSize:12 }}>
                  {ENTITY_ICON[log.entity_type] || '•'}
                  <span style={{ marginLeft:5, color:"var(--text-sub)", textTransform:"capitalize" }}>{log.entity_type}</span>
                </TD>
                <TD mono style={{ fontSize:11 }}>{log.entity_id ?? '—'}</TD>
                <TD><ActionBadge action={log.action} /></TD>
                <TD style={{ fontSize:11, color:"var(--text-sub)", maxWidth:200, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                  {log.detail ?? '—'}
                </TD>
              </tr>
            )) : (
              <tr>
                <td colSpan={6} style={{ padding:"28px 14px", textAlign:"center", fontSize:12, color:"var(--text-dim)" }}>
                  {loading ? 'Loading…' : 'No activity recorded yet.'}
                </td>
              </tr>
            )}
          </tbody>
        </TableWrap>

        <div style={{ background:"var(--surface)", border:"1px solid var(--border)", borderRadius:9, padding:16, alignSelf:"start" }}>
          <div style={{ fontSize:10, fontWeight:700, color:"var(--text-dim)", textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:12 }}>Event Detail</div>
          {selected ? (
            <>
              <div style={{ marginBottom:12 }}><ActionBadge action={selected.action} /></div>
              {[
                ["When",      fmt(selected.created_at), true ],
                ["Actor",     selected.actor,           false],
                ["Role",      selected.actor_role,      false],
                ["Type",      selected.entity_type,     false],
                ["Entity ID", selected.entity_id,       true ],
              ].map(([k,v,mono]) => (
                <div key={k} style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", padding:"7px 0", borderBottom:"1px solid var(--border)" }}>
                  <span style={{ fontSize:11, color:"var(--text-sub)", flexShrink:0 }}>{k}</span>
                  <span style={{ fontSize:11, fontWeight:500, color:"var(--ink)", fontFamily:mono?"'IBM Plex Mono',monospace":"inherit", textAlign:"right", marginLeft:8, wordBreak:"break-all" }}>{v??'—'}</span>
                </div>
              ))}
              {selected.detail && (
                <div style={{ paddingTop:10 }}>
                  <div style={{ fontSize:10, fontWeight:700, color:"var(--text-dim)", textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:6 }}>Description</div>
                  <div style={{ fontSize:12, color:"var(--text)", lineHeight:1.6 }}>{selected.detail}</div>
                </div>
              )}
            </>
          ) : (
            <div style={{ textAlign:"center", paddingTop:40, color:"var(--text-dim)", fontSize:12 }}>
              <div style={{ fontSize:22, marginBottom:8 }}>📋</div>
              Select an event<br/>to view details
            </div>
          )}
        </div>
      </div>
    </>
  );
}
